// src/cockpit4/components/KnowledgeMap.tsx
// ============================================
// P1-功能6：知识地图与缺口分析（知识中心）
//
// 功能：
//   1. 按领域/部门/能力的知识地图（树形结构展示知识分类）
//   2. 知识缺口识别（哪些领域知识不足，按笔记数<阈值标记缺口）
//   3. 知识优先级排序（缺口领域按重要性排序）
//   4. 知识建设路线图（生成建设计划 Markdown）
//   5. 知识覆盖率统计（各领域笔记数/目标数的覆盖率百分比）
//
// 数据来源：
//   - /vault-api/list?dir=09_Knowledge（知识中心） 递归扫描
//
// 缺口识别规则：
//   - 笔记数 < GAP_THRESHOLD（默认 5） → 标记为"知识缺口"
//   - 笔记数 < TARGET_COUNT（默认 10） → 覆盖率不足
//   - 优先级 = 重要性权重 × 缺口严重程度
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Network,
  RefreshCw,
  Loader2,
  X,
  Folder,
  FolderOpen,
  FileText,
  AlertTriangle,
  Target,
  TrendingUp,
  Map as MapIcon,
  ChevronRight,
  ChevronDown,
  Download,
  Copy,
  Check,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 知识地图节点（树形） */
interface MapNode {
  /** 节点名称 */
  name: string;
  /** Vault 相对路径 */
  path: string;
  /** 是否为目录 */
  isDirectory: boolean;
  /** 子节点 */
  children: MapNode[];
  /** 该目录直接 .md 文件数 */
  directCount: number;
  /** 递归 .md 文件总数（含子目录） */
  totalCount: number;
  /** 节点深度 */
  depth: number;
}

/** 缺口分析项 */
interface GapItem {
  /** 节点路径 */
  path: string;
  /** 节点名称 */
  name: string;
  /** 当前笔记数 */
  count: number;
  /** 目标笔记数 */
  target: number;
  /** 覆盖率（0-100） */
  coverage: number;
  /** 优先级（0-100，越高越优先） */
  priority: number;
  /** 重要性权重（基于层级和分类） */
  weight: number;
  /** 节点深度 */
  depth: number;
}

// ============================================
// 常量
// ============================================

/** Vault 知识中心根路径 */
const KNOWLEDGE_ROOT = "09_Knowledge（知识中心）";

/** 缺口阈值：低于此值视为知识缺口 */
const GAP_THRESHOLD = 5;

/** 目标笔记数：每个领域期望达到的笔记数 */
const TARGET_COUNT = 10;

/** 最大递归深度 */
const MAX_DEPTH = 5;

/** 重要性权重表（按目录名匹配，未匹配默认 50） */
const WEIGHT_MAP: Record<string, number> = {
  Cards: 80,
  Domain: 90,
  Methodology: 85,
  FAQ: 70,
  Meetings: 60,
  Projects: 65,
  Research: 75,
  Summaries: 55,
  Outputs: 50,
  Reviews: 60,
  Ideas: 60,
  Prompts: 50,
};

// ============================================
// 主组件
// ============================================

export default function KnowledgeMap() {
  // 知识地图根节点
  const [root, setRoot] = useState<MapNode | null>(null);
  // 加载状态
  const [loading, setLoading] = useState(true);
  // 展开的节点 path 集合
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  // 选中的节点 path
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  // 是否显示路线图 Modal
  const [showRoadmap, setShowRoadmap] = useState(false);
  // 复制状态
  const [copied, setCopied] = useState(false);

  // ============================================
  // 递归扫描知识中心
  // ============================================
  const scanKnowledge = useCallback(async (): Promise<MapNode | null> => {
    const scanDir = async (dirPath: string, depth: number): Promise<MapNode> => {
      const node: MapNode = {
        name: dirPath.split("/").pop() || dirPath,
        path: dirPath,
        isDirectory: true,
        children: [],
        directCount: 0,
        totalCount: 0,
        depth,
      };
      try {
        const url = `/vault-api/list?dir=${encodeURIComponent(dirPath)}&ext=.md`;
        const resp = await fetch(url);
        if (!resp.ok) return node;
        const json = await resp.json();
        if (!json?.success || !Array.isArray(json.files)) return node;

        const subDirs: string[] = [];
        for (const f of json.files) {
          if (f.isDirectory) {
            subDirs.push(f.path);
          } else if (f.name && f.name.endsWith(".md")) {
            node.directCount++;
          }
        }
        // 递归子目录（限制深度）
        if (depth < MAX_DEPTH) {
          for (const sub of subDirs) {
            const childNode = await scanDir(sub, depth + 1);
            node.children.push(childNode);
          }
        }
        // 子目录按名排序
        node.children.sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
        // 计算总数
        node.totalCount = node.directCount;
        for (const child of node.children) {
          node.totalCount += child.totalCount;
        }
      } catch {
        // 单目录扫描失败，返回空节点
      }
      return node;
    };

    const rootNode = await scanDir(KNOWLEDGE_ROOT, 0);
    // 修正根节点名（避免显示完整路径）
    rootNode.name = KNOWLEDGE_ROOT;
    return rootNode;
  }, []);

  const loadMap = useCallback(async () => {
    setLoading(true);
    try {
      const mapRoot = await scanKnowledge();
      setRoot(mapRoot);
      // 默认展开根节点和第一层子目录
      if (mapRoot) {
        const initial = new Set<string>([mapRoot.path]);
        for (const child of mapRoot.children) {
          initial.add(child.path);
        }
        setExpandedPaths(initial);
      }
    } catch (err) {
      console.warn("[KnowledgeMap] 扫描知识中心失败:", err);
      setRoot(null);
    } finally {
      setLoading(false);
    }
  }, [scanKnowledge]);

  useEffect(() => {
    void loadMap();
  }, [loadMap]);

  // ============================================
  // 缺口分析：找出所有有缺口（笔记数 < target）的目录节点
  // ============================================
  const gapItems = useMemo<GapItem[]>(() => {
    if (!root) return [];
    const items: GapItem[] = [];
    const collect = (node: MapNode) => {
      // 只分析目录节点（叶子文件不参与缺口分析）
      if (node.isDirectory && node.depth >= 1) {
        const count = node.totalCount;
        // 仅对包含内容的目录或空目录做分析（避免无穷尽空目录）
        const target = TARGET_COUNT;
        const coverage = target > 0 ? Math.min(Math.round((count / target) * 100), 100) : 0;
        const weight = WEIGHT_MAP[node.name] || 50 - node.depth * 5;
        // 优先级 = 重要性权重 × 缺口严重程度
        const gapSeverity = count < GAP_THRESHOLD ? 1.0 : count < target ? 0.6 : 0;
        const priority = Math.round(weight * gapSeverity);
        // 仅加入有缺口的节点（coverage < 100%）
        if (coverage < 100) {
          items.push({
            path: node.path,
            name: node.name,
            count,
            target,
            coverage,
            priority,
            weight,
            depth: node.depth,
          });
        }
      }
      for (const child of node.children) {
        collect(child);
      }
    };
    collect(root);
    // 按优先级降序排序
    items.sort((a, b) => b.priority - a.priority);
    return items;
  }, [root]);

  // ============================================
  // 覆盖率统计
  // ============================================
  const coverageStats = useMemo(() => {
    if (!root) {
      return { totalNodes: 0, gapNodes: 0, fullCoverage: 0, avgCoverage: 0, totalNotes: 0 };
    }
    let totalNodes = 0;
    let gapNodes = 0;
    let fullCoverage = 0;
    let coverageSum = 0;
    let totalNotes = 0;
    const collect = (node: MapNode) => {
      if (node.isDirectory && node.depth >= 1) {
        totalNodes++;
        totalNotes += node.totalCount;
        const coverage =
          TARGET_COUNT > 0
            ? Math.min(Math.round((node.totalCount / TARGET_COUNT) * 100), 100)
            : 0;
        coverageSum += coverage;
        if (coverage < 100) gapNodes++;
        if (coverage >= 100) fullCoverage++;
      }
      for (const child of node.children) collect(child);
    };
    collect(root);
    return {
      totalNodes,
      gapNodes,
      fullCoverage,
      avgCoverage: totalNodes > 0 ? Math.round(coverageSum / totalNodes) : 0,
      totalNotes,
    };
  }, [root]);

  // ============================================
  // 生成建设路线图（Markdown）
  // ============================================
  const roadmapMarkdown = useMemo(() => {
    if (gapItems.length === 0) {
      return "# 知识建设路线图\n\n✅ 当前知识库覆盖率良好，暂无重大缺口。\n";
    }
    const parts: string[] = [];
    parts.push("# 知识建设路线图");
    parts.push("");
    parts.push(`> 生成时间：${new Date().toISOString()}`);
    parts.push(`> 数据来源：${KNOWLEDGE_ROOT} 递归扫描`);
    parts.push(`> 缺口阈值：${GAP_THRESHOLD} 篇 · 目标篇数：${TARGET_COUNT} 篇`);
    parts.push("");
    parts.push("## 总览");
    parts.push("");
    parts.push(`- 总领域数：${coverageStats.totalNodes}`);
    parts.push(`- 已达标领域：${coverageStats.fullCoverage}`);
    parts.push(`- 缺口领域：${coverageStats.gapNodes}`);
    parts.push(`- 平均覆盖率：${coverageStats.avgCoverage}%`);
    parts.push(`- 总笔记数：${coverageStats.totalNotes}`);
    parts.push("");
    parts.push("## 优先建设清单（按优先级降序）");
    parts.push("");

    // 按优先级分组：高/中/低
    const high = gapItems.filter((g) => g.priority >= 60);
    const medium = gapItems.filter((g) => g.priority >= 30 && g.priority < 60);
    const low = gapItems.filter((g) => g.priority < 30);

    if (high.length > 0) {
      parts.push("### 🔴 高优先级（重要且缺口严重）");
      parts.push("");
      for (const g of high) {
        parts.push(
          `- [ ] **${g.name}**（${g.path}）：${g.count}/${g.target} 篇，覆盖率 ${g.coverage}%，优先级 ${g.priority}`
        );
      }
      parts.push("");
    }
    if (medium.length > 0) {
      parts.push("### 🟡 中优先级");
      parts.push("");
      for (const g of medium) {
        parts.push(
          `- [ ] **${g.name}**（${g.path}）：${g.count}/${g.target} 篇，覆盖率 ${g.coverage}%，优先级 ${g.priority}`
        );
      }
      parts.push("");
    }
    if (low.length > 0) {
      parts.push("### 🟢 低优先级");
      parts.push("");
      for (const g of low) {
        parts.push(
          `- [ ] **${g.name}**（${g.path}）：${g.count}/${g.target} 篇，覆盖率 ${g.coverage}%，优先级 ${g.priority}`
        );
      }
      parts.push("");
    }
    parts.push("## 建议执行步骤");
    parts.push("");
    parts.push("1. 优先补全高优先级缺口领域，每周完成 1-2 个领域");
    parts.push("2. 利用「知识自动生产」组件批量生成知识卡片");
    parts.push("3. 通过「知识使用反馈闭环」持续优化已有知识");
    parts.push("4. 每月复查本路线图，更新覆盖率和优先级");
    parts.push("");
    parts.push("---");
    parts.push(`*由知识地图与缺口分析组件生成。*`);
    return parts.join("\n");
  }, [gapItems, coverageStats]);

  // ============================================
  // 操作：展开/收起
  // ============================================
  const toggleExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // 复制路线图到剪贴板
  const handleCopyRoadmap = useCallback(() => {
    try {
      navigator.clipboard.writeText(roadmapMarkdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("[KnowledgeMap] 复制路线图失败:", err);
    }
  }, [roadmapMarkdown]);

  // 下载路线图为 .md 文件
  const handleDownloadRoadmap = useCallback(() => {
    try {
      const blob = new Blob([roadmapMarkdown], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `知识建设路线图-${new Date().toISOString().slice(0, 10)}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("[KnowledgeMap] 下载路线图失败:", err);
    }
  }, [roadmapMarkdown]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-indigo-500/15 border border-indigo-500/30">
            <Network size={13} className="text-indigo-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">知识地图与缺口分析</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Map · 树形结构 · 缺口识别 · 优先级排序 · 建设路线图
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowRoadmap(true)}
            disabled={!root || gapItems.length === 0}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-500/15 border border-indigo-500/30 hover:bg-indigo-500/25 transition-colors disabled:opacity-30"
            title="生成知识建设路线图"
          >
            <MapIcon size={11} className="text-indigo-400" />
            <span className="text-[9px] font-mono text-indigo-300 uppercase tracking-wider">
              建设路线图
            </span>
          </button>
          <button
            onClick={loadMap}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-indigo-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-indigo-400" />
            )}
            <span className="text-[9px] font-mono text-indigo-300 uppercase tracking-wider">
              {loading ? "扫描中..." : "刷新"}
            </span>
          </button>
        </div>
      </div>

      {/* 覆盖率统计面板 */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.04] bg-white/[0.01] flex-wrap">
        <CoverageStat
          label="总领域"
          value={coverageStats.totalNodes}
          color="#A78BFA"
          icon={Folder}
        />
        <CoverageStat
          label="已达标"
          value={coverageStats.fullCoverage}
          color="#10B981"
          icon={Target}
        />
        <CoverageStat
          label="缺口领域"
          value={coverageStats.gapNodes}
          color="#F87171"
          icon={AlertTriangle}
        />
        <CoverageStat
          label="平均覆盖率"
          value={`${coverageStats.avgCoverage}%`}
          color="#38BDF8"
          icon={TrendingUp}
        />
        <CoverageStat
          label="总笔记数"
          value={coverageStats.totalNotes}
          color="#FBBF24"
          icon={FileText}
        />
      </div>

      {/* 主内容：左右分栏（树形 + 缺口列表） */}
      <div className="flex-1 overflow-hidden flex">
        {/* 左侧：知识地图树 */}
        <div className="flex-1 flex flex-col border-r border-white/[0.04] min-w-0">
          <div className="px-3 py-2 border-b border-white/[0.04] bg-white/[0.01]">
            <div className="flex items-center gap-1.5">
              <Network size={11} className="text-indigo-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                知识地图（递归树）
              </span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1 min-h-0">
            {loading ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <Loader2 size={20} className="animate-spin text-indigo-400 mb-2" />
                <span className="text-[11px] font-mono">正在扫描知识中心...</span>
              </div>
            ) : !root ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <Network size={28} className="text-gray-700 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">
                  暂无数据，知识库暂未接入
                </p>
              </div>
            ) : (
              <MapTreeNode
                node={root}
                expandedPaths={expandedPaths}
                selectedPath={selectedPath}
                onToggleExpand={toggleExpand}
                onSelect={setSelectedPath}
              />
            )}
          </div>
        </div>

        {/* 右侧：缺口分析列表 */}
        <div className="w-80 flex-shrink-0 flex flex-col">
          <div className="px-3 py-2 border-b border-white/[0.04] bg-white/[0.01]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <AlertTriangle size={11} className="text-amber-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  缺口清单（按优先级）
                </span>
              </div>
              <span className="text-[9px] font-mono text-gray-600">{gapItems.length} 项</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-0">
            {!loading && gapItems.length === 0 ? (
              <div className="text-center py-8 text-[11px] font-mono text-gray-500">
                {root ? "✅ 暂无缺口" : "请先扫描知识中心"}
              </div>
            ) : (
              gapItems.map((g, idx) => (
                <GapCard
                  key={`${g.path}-${idx}`}
                  item={g}
                  isSelected={selectedPath === g.path}
                  onClick={() => setSelectedPath(g.path)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* 底部数据来源说明 */}
      <div className="px-4 py-1.5 border-t border-white/[0.04]">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：/vault-api/list?dir={KNOWLEDGE_ROOT} 递归扫描 · 缺口阈值 {GAP_THRESHOLD} 篇 · 目标 {TARGET_COUNT} 篇
        </span>
      </div>

      {/* 路线图 Modal */}
      <AnimatePresence>
        {showRoadmap && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setShowRoadmap(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border border-indigo-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <MapIcon size={14} className="text-indigo-400 flex-shrink-0" />
                  <h3 className="text-[14px] font-semibold text-white">知识建设路线图</h3>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleCopyRoadmap}
                    title="复制到剪贴板"
                    className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
                  >
                    {copied ? (
                      <Check size={11} className="text-emerald-400" />
                    ) : (
                      <Copy size={11} className="text-gray-400" />
                    )}
                    <span className="text-[9px] font-mono text-gray-400 uppercase">
                      {copied ? "已复制" : "复制"}
                    </span>
                  </button>
                  <button
                    onClick={handleDownloadRoadmap}
                    title="下载 .md 文件"
                    className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
                  >
                    <Download size={11} className="text-gray-400" />
                    <span className="text-[9px] font-mono text-gray-400 uppercase">下载</span>
                  </button>
                  <button
                    onClick={() => setShowRoadmap(false)}
                    className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-5">
                <pre className="text-[12px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                  {roadmapMarkdown}
                </pre>
              </div>
              <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] bg-white/[0.01]">
                <span className="text-[10px] font-mono text-gray-600">
                  基于实际 Vault 扫描结果生成 · 可直接保存为 .md 文件
                </span>
                <span className="text-[10px] font-mono text-gray-600">
                  {gapItems.length} 项缺口 · {coverageStats.avgCoverage}% 平均覆盖率
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：覆盖率统计
// ============================================
function CoverageStat({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  color: string;
  icon: typeof Folder;
}) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/[0.04] bg-white/[0.02]">
      <Icon size={9} style={{ color }} />
      <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      <span className="text-[11px] font-mono font-semibold" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

// ============================================
// 子组件：地图树节点（递归）
// ============================================
interface MapTreeNodeProps {
  node: MapNode;
  expandedPaths: Set<string>;
  selectedPath: string | null;
  onToggleExpand: (path: string) => void;
  onSelect: (path: string) => void;
}

function MapTreeNode({
  node,
  expandedPaths,
  selectedPath,
  onToggleExpand,
  onSelect,
}: MapTreeNodeProps) {
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = selectedPath === node.path;
  const hasChildren = node.children.length > 0;
  // 缺口判断
  const isGap = node.depth >= 1 && node.totalCount < GAP_THRESHOLD;
  const coverage =
    TARGET_COUNT > 0 ? Math.min(Math.round((node.totalCount / TARGET_COUNT) * 100), 100) : 0;
  // 缩进
  const paddingLeft = 8 + node.depth * 12;

  return (
    <div>
      <div
        className={`w-full text-left py-1.5 flex items-center justify-between hover:bg-white/[0.03] transition-colors group ${
          isSelected ? "bg-indigo-500/10" : ""
        }`}
        style={{ paddingLeft, paddingRight: 8 }}
      >
        <div className="flex items-center gap-1 min-w-0">
          {/* 展开/收起箭头 */}
          {hasChildren ? (
            <button
              onClick={() => onToggleExpand(node.path)}
              className="flex-shrink-0 text-gray-600 hover:text-indigo-400 transition-colors"
              title={isExpanded ? "收起" : "展开"}
            >
              {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            </button>
          ) : (
            <span className="w-[10px] flex-shrink-0" />
          )}
          <button
            onClick={() => onSelect(node.path)}
            className={`flex items-center gap-1 min-w-0 truncate ${
              isSelected ? "text-indigo-300" : "text-gray-300"
            }`}
            title={node.path}
          >
            {hasChildren ? (
              isExpanded ? (
                <FolderOpen size={11} className="text-indigo-400 flex-shrink-0" />
              ) : (
                <Folder size={11} className="text-gray-500 flex-shrink-0" />
              )
            ) : (
              <FileText size={11} className="text-gray-600 flex-shrink-0" />
            )}
            <span className="text-[11px] truncate">{node.name}</span>
            {isGap && (
              <AlertTriangle
                size={9}
                className="text-amber-400 flex-shrink-0 ml-0.5"
              />
            )}
          </button>
        </div>
        {/* 笔记数 + 覆盖率徽章 */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
              isGap
                ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                : coverage >= 100
                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                : "bg-white/[0.03] text-gray-500 border border-white/[0.04]"
            }`}
            title={`笔记数 ${node.totalCount} · 目标 ${TARGET_COUNT} · 覆盖率 ${coverage}%`}
          >
            {node.totalCount}/{TARGET_COUNT}
          </span>
          <span className="text-[9px] font-mono text-gray-600 w-8 text-right">
            {coverage}%
          </span>
        </div>
      </div>
      {/* 递归渲染子节点 */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <MapTreeNode
              key={child.path}
              node={child}
              expandedPaths={expandedPaths}
              selectedPath={selectedPath}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// 子组件：缺口卡片
// ============================================
function GapCard({
  item,
  isSelected,
  onClick,
}: {
  item: GapItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  // 优先级等级
  const level = item.priority >= 60 ? "high" : item.priority >= 30 ? "medium" : "low";
  const levelColor =
    level === "high" ? "#F87171" : level === "medium" ? "#FBBF24" : "#6B7280";
  const levelLabel = level === "high" ? "高" : level === "medium" ? "中" : "低";

  return (
    <motion.button
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className={`w-full text-left rounded-md border p-2 transition-colors ${
        isSelected
          ? "border-indigo-500/40 bg-indigo-500/[0.06]"
          : "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1 min-w-0">
          <AlertTriangle size={10} style={{ color: levelColor }} className="flex-shrink-0" />
          <span className="text-[11px] text-gray-200 truncate">{item.name}</span>
        </div>
        <span
          className="flex-shrink-0 px-1.5 py-0.5 rounded text-[8px] font-mono font-bold"
          style={{
            background: `${levelColor}20`,
            color: levelColor,
          }}
        >
          {levelLabel}
        </span>
      </div>
      {/* 覆盖率进度条 */}
      <div className="relative h-1 bg-white/[0.04] rounded-full overflow-hidden mb-1">
        <div
          className="absolute top-0 left-0 h-full rounded-full transition-all"
          style={{ width: `${item.coverage}%`, background: levelColor }}
        />
      </div>
      <div className="flex items-center justify-between text-[9px] font-mono">
        <span className="text-gray-500">
          {item.count}/{item.target} 篇
        </span>
        <span className="text-gray-500">优先级 {item.priority}</span>
        <span style={{ color: levelColor }}>{item.coverage}%</span>
      </div>
      <div className="mt-1 text-[8px] font-mono text-gray-700 truncate" title={item.path}>
        📁 {item.path}
      </div>
    </motion.button>
  );
}
